import React from "react";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import { COMPONENT_DATA_LOCAL_KEY } from "../MappingEditor/consts";
import { useLocation, useNavigate } from "react-router";
import { searchStore } from "features/ProductSearch/SearchStore";
import { getCurrentReferenceUnitValue } from "../MappingEditor/utils";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import EditorLayout from "layouts/EditorLayout";
import EditorHeader from "./components/EditorHeader";
import ProductUsageWarning from "features/ProductUsage/ProductUsageWarning";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import {
  FileCategoryType,
  ResultOUT,
  S3PostData,
  SingleItem,
} from "api-client";
import { useTranslation } from "react-i18next";
import useToast from "hooks/useToast";
import useMappingEditor from "features/MappingEditor/useMappingEditor";
import { AxiosError } from "axios";
import { useMappingTools } from "features/MappingEditor/useMappingTools";
import LoadingIcon from "components/LoadingIcon";
import ComponentHeader from "./components/ComponentHeader";
import { CustomComponent, COEStore } from "./ComponentEditorStore";
import GeneralInformation from "./features/GeneralInformation";
import ReferenceUnitAndProducts from "./features/ًReferenceUnitAndProducts";
import CustomProperties from "features/PropertyManager/features/CustomProperties";
import { propertyManagerStore } from "features/PropertyManager/PropertyManagerStore";
import ComponentGraphic from "./features/ComponentGraphic";
import { useUploadToS3 } from "hooks/useUploadToS3";
import { ProductActionMode } from "utils";

const ComponentEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { mappingToObjects } = useMappingEditor();
  const { onCloseEditor } = useMappingTools();
  const [loading, setLoading] = React.useState(true);
  const { uploadFiles: uploadToS3 } = useUploadToS3();

  async function getPropertyConfigs() {
    await propertyManagerStore.getAllPropertyConfigs();
    COEStore.onChangeProperties(propertyManagerStore.allPropertyConfigs);
  }

  async function initialLoad() {
    setLoading(true);
    if (!BPEStore.optionsLists.din_category.length) {
      await BPEStore.getDinCategoriesLevel1();
    }

    const lastState = JSON.parse(
      localStorage.getItem(COMPONENT_DATA_LOCAL_KEY) || "{}"
    );

    if (lastState.data) {
      recallState(lastState.data);
    } else if (location.state) {
      await getPropertyConfigs();
      loadDataForUpdate();
      await COEStore.getComponentCompliance();
    } else {
      COEStore.resetData();
      await getPropertyConfigs();
    }

    COEStore.checkSaveValidation();
    setLoading(false);
  }

  React.useEffect(() => {
    (async () => await initialLoad())();

    return () => {
      COEStore.resetData();
    };
  }, []);

  function recallState(data: CustomComponent) {
    COEStore.setData(data);
    localStorage.removeItem(COMPONENT_DATA_LOCAL_KEY);
    if (COEStore.onTheFlyProductAdded) {
      COEStore.addChildItem(COEStore.onTheFlyProductAdded);
      COEStore.setOnTheFlyProductAdded(undefined);
    }
  }

  const getDINCategoryID = (din_category: string) => {
    const currentItemID = BPEStore.optionsLists.din_category.find(
      (item) => item.name === din_category
    )?.id;
    return currentItemID;
  };

  const uploadDiagram = async () => {
    const fileToUpload = COEStore.diagramToUpload;
    if (!fileToUpload) {
      COEStore.setUploadedDiagramIDs([]);
      return;
    }

    const filesMeta = {
      file_name: fileToUpload.name,
      content_type: fileToUpload?.type,
      category: FileCategoryType.Diagram,
    };

    const presignedPosts: void | S3PostData[] = await BPEStore.uploadFilesInfo([
      filesMeta,
    ]);
    if (!presignedPosts) return;
    const result = await uploadToS3([fileToUpload], presignedPosts);
    if (!result) return;
    const uploadedFilesIDs = result.map((item) => item.file_id);
    COEStore.setUploadedDiagramIDs(uploadedFilesIDs);
  };

  function loadDataForUpdate() {
    const currentItem = searchStore.componentDetails;

    COEStore.setData({
      id: currentItem?.id,
      name: currentItem?.name,
      auto_mapping_tags:
        currentItem?.automapping_tags?.map((item) => ({
          id: item,
          name: item,
        })) ?? [],
      reference_unit: getCurrentReferenceUnitValue(
        currentItem?.reference_unit.toUpperCase()
      ),
      childrenList: currentItem?.children?.map((item) => ({
        ...item,
        user_lifespan: item.life_span,
      })),
      preuse_qualification: BPEStore.optionsLists.preuse_qualification.find(
        (item) => item.name === currentItem?.preuse_qualification
      )?.id,
      deconstructability_qualification:
        BPEStore.optionsLists.deconstructability_qualification.find((item) =>
          item.name?.includes(
            currentItem?.deconstructability_qualification as string
          )
        )?.id,
      reusability_qualification:
        BPEStore.optionsLists.reusability_qualification.find(
          (item) => item.name === currentItem?.reusability_qualification
        )?.id,
      compliance: {
        compliant_certifications: currentItem?.compliance?.certifications,
        conformities: currentItem?.compliance?.conformities,
        dataset_sources: currentItem?.compliance?.dataset_sources,
      },
      din_category: currentItem?.din_category_level_1
        ? getDINCategoryID(
            `${currentItem?.din_category_level_1_number ?? ""} ${
              currentItem?.din_category_level_1 ?? ""
            }`
          )
        : null,
      properties: COEStore.data.properties.map((item) => ({
        id: item.slug,
        slug: item.slug,
        name: item.name,
        property_type: item.property_type,
        value: currentItem?.org_properties?.[item.slug]?.value,
        options: item.options,
      })),
    } as CustomComponent);
  }

  async function updateComponent(doMatch: boolean | undefined) {
    const response = await COEStore.updateComponent();
    if (doMatch) {
      await mappingToObjects(
        "manual_match",
        response.data.component.id,
        MEStore.currentRow?.original as SingleItem
      );
    }
    if (response.data.is_recalculation_triggered) {
      toast(t("components.successMsgOnEditWithRecalculation"), "success");
    } else {
      toast(t("components.successMsgOnEdit"), "success");
    }
  }

  async function createComponent(doMatch: boolean | undefined) {
    const createdItem = await COEStore.registerComponent();
    if (doMatch && createdItem) {
      await mappingToObjects(
        "manual_match",
        createdItem.id,
        MEStore.currentRow?.original as SingleItem
      );
    }
    toast(t("components.successMsgOnSave"), "success");
  }

  const saveAndClose = async (doMatch?: boolean) => {
    await uploadDiagram();
    try {
      if (location.state?.mode === ProductActionMode.Edit) {
        await updateComponent(doMatch);
      } else {
        await createComponent(doMatch);
      }
      searchStore.setComponentDetails(undefined);
      COEStore.resetData();
      onCloseEditor(undefined, doMatch, location.state?.isDetails);
    } catch (error) {
      toast(
        `${t("components.warningMsgOnSave")} ${
          (
            ((error as AxiosError)?.response?.data as ResultOUT)
              ?.message as ResultOUT
          )?.message ?? error
        }`,
        "warning"
      );
    }
  };

  const handleUpgradeComponent = async () => {
    const response = await COEStore.upgradeComponent();
    const newId = response.data?.component?.id;

    if (!newId) {
      throw new Error(t("components.warningMsgOnSave"));
    }

    const details = await searchStore.getComponentDetails(newId);
    if (!details) {
      throw new Error(t("components.warningMsgOnSave"));
    }

    loadDataForUpdate();
    await COEStore.getComponentCompliance();

    navigate(".", {
      replace: true,
      state: {
        ...location.state,
        id: newId,
      },
    });

    if (response.data?.is_recalculation_triggered) {
      toast(t("components.successMsgOnUpgradeWithRecalculation"), "success");
    } else {
      toast(t("components.successMsgOnUpgrade"), "success");
    }
  };

  const columnStyle = clsx(
    "px-6 pt-6 text-gray-800 xl:max-h-[calc(100vh-145px)] max-h-auto lg:overflow-y-auto"
  );

  return (
    <EditorLayout
      header={
        <div>
          <EditorHeader
            saveAndClose={saveAndClose}
            upgradeComponent={handleUpgradeComponent}
          />
          <ComponentHeader name={COEStore.data.name} />
        </div>
      }
      className={clsx(
        "xl:!grid-cols-2 lg:!grid-cols-1 !grid-cols-1 !max-h-[calc(100vh-135px)] !min-h-[calc(100vh-135px)]",
        "lg:max-h-full bg-white overflow-y-auto",
        { "!flex w-full items-center justify-center": loading }
      )}
    >
      {loading ? (
        <LoadingIcon type="secondary" className="m-auto !w-20 !h-20" />
      ) : (
        <>
          <div
            className={clsx(
              columnStyle,
              "lg:border-r xl:pb-6 xl:col-span-1 lg:col-span-2"
            )}
          >
            {location.state?.mode === ProductActionMode.Edit && (
              <ProductUsageWarning id={COEStore.data.id ?? ""} />
            )}
            <GeneralInformation />
            <CustomProperties isComponent />
            <ComponentGraphic />
          </div>
          <div className={clsx(columnStyle, "xl:col-span-1 lg:col-span-3")}>
            <ReferenceUnitAndProducts />
          </div>
        </>
      )}
    </EditorLayout>
  );
};

export default observer(ComponentEditor);
